import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';

import { Radius, Spacing, Typography } from 'chatie-design-system';

import { voteCommunityPoll } from '@api/community';
import { ICommunityPoll } from '@model';

const wrapperCSS = (theme: Theme) => css`
  margin-top: ${Spacing.small}px;
  border: 1px solid ${theme.border.light};
  border-radius: ${Radius.medium}px;
  padding: ${Spacing.medium}px;
`;

const optionsListCSS = css`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.small}px;
`;

const optionBtnCSS =
  (isSelected: boolean, showResult: boolean, isWinner: boolean) =>
  (theme: Theme) =>
    css`
      width: 100%;
      text-align: left;
      padding: 10px ${Spacing.small}px;
      border: 1px solid
        ${isSelected ? theme.colors.main500 : theme.border.light};
      border-radius: ${Radius.small}px;
      ${Typography.body14};
      cursor: ${showResult ? 'default' : 'pointer'};
      position: relative;
      overflow: hidden;
      background-color: transparent;

      ${isWinner &&
      showResult &&
      css`
        border-color: ${theme.colors.main500};
        font-weight: 600;
      `}

      ${!showResult &&
      css`
        &:active {
          border-color: ${theme.colors.main500};
          background-color: ${theme.colors.main50};
        }
      `}

      ${!showResult &&
      isSelected &&
      css`
        background-color: ${theme.colors.main50};
      `}
    `;

const optionBarCSS =
  (percentage: number, isWinner: boolean) => (theme: Theme) =>
    css`
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: ${percentage}%;
      background-color: ${isWinner ? theme.colors.main100 : theme.var.gray100};
      transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
      border-radius: ${Radius.small}px;
    `;

const optionContentCSS = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 1;
`;

const optionTextCSS = css`
  display: flex;
  align-items: center;
  gap: ${Spacing.xsmall}px;
  min-width: 0;
  flex: 1;
`;

const checkMarkCSS = (theme: Theme) => css`
  color: ${theme.colors.main500};
  font-size: 14px;
  flex-shrink: 0;
`;

const voteCountCSS = (isWinner: boolean) => (theme: Theme) =>
  css`
    ${Typography.body12};
    color: ${isWinner ? theme.colors.main500 : theme.var.gray500};
    flex-shrink: 0;
    margin-left: ${Spacing.xsmall}px;
    font-weight: ${isWinner ? 600 : 400};
  `;

const footerCSS = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${Spacing.small}px;
`;

const footerLeftCSS = css`
  display: flex;
  align-items: center;
  gap: ${Spacing.xsmall}px;
  min-width: 0;
  flex: 1;
`;

const totalVotesCSS = (theme: Theme) => css`
  ${Typography.body12};
  color: ${theme.var.gray500};
`;

const remainingTimeCSS = (theme: Theme) => css`
  ${Typography.body12};
  color: ${theme.var.gray400};
  display: flex;
  align-items: center;
  gap: 2px;

  &::before {
    content: '·';
    margin: 0 2px;
  }
`;

const closedBadgeCSS = (theme: Theme) => css`
  ${Typography.body12};
  color: ${theme.var.gray400};
  font-weight: 600;
  flex-shrink: 0;
`;

const voteBtnCSS = (theme: Theme) => css`
  padding: ${Spacing.xsmall}px ${Spacing.medium}px;
  border-radius: ${Radius.medium}px;
  background-color: ${theme.colors.main500};
  color: ${theme.true.white};
  ${Typography.body14};
  font-weight: 600;
  flex-shrink: 0;

  &:disabled {
    background-color: ${theme.var.gray300};
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    opacity: 0.85;
  }
`;

function formatRemainingTime(
  endsAt: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): string | null {
  const now = new Date();
  const end = new Date(endsAt);
  const diffMs = end.getTime() - now.getTime();

  if (diffMs <= 0) return null;

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) {
    return hours > 0
      ? `${days}${t('일')} ${hours}${t('시간')} ${t('남음')}`
      : `${days}${t('일')} ${t('남음')}`;
  }
  if (hours > 0) {
    return minutes > 0
      ? `${hours}${t('시간')} ${minutes}${t('분')} ${t('남음')}`
      : `${hours}${t('시간')} ${t('남음')}`;
  }
  if (minutes > 0) {
    return `${minutes}${t('분')} ${t('남음')}`;
  }
  return t('곧 마감');
}

interface PollDisplayProps {
  poll: ICommunityPoll;
  communityId: number;
  onVote?: (poll: ICommunityPoll, myVoteOptionId: number) => void;
}

const PollDisplay = ({ poll, communityId, onVote }: PollDisplayProps) => {
  const { t } = useTranslation();

  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null);
  const [, setTick] = useState(0);

  // 잔여시간 1분마다 갱신
  useEffect(() => {
    if (!poll.endsAt || poll.isClosed) return;
    const interval = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(interval);
  }, [poll.endsAt, poll.isClosed]);

  const { mutate: vote, isLoading: isVoting } = useMutation(
    (optionId: number) => voteCommunityPoll(communityId, optionId),
    {
      onSuccess: (data) => {
        onVote?.(data.poll, data.myVoteOptionId);
      },
    }
  );

  const hasVoted = poll.myVoteOptionId !== null;
  const showResult = hasVoted || poll.isClosed || poll.canViewResult;

  const totalVotes = useMemo(
    () => poll.options.reduce((sum, opt) => sum + (opt.voteCount ?? 0), 0),
    [poll.options]
  );

  const maxVoteCount = useMemo(
    () => Math.max(...poll.options.map((opt) => opt.voteCount ?? 0)),
    [poll.options]
  );

  const getPercentage = useCallback(
    (voteCount: number | null) => {
      if (!totalVotes || voteCount === null) return 0;
      return Math.round((voteCount / totalVotes) * 100);
    },
    [totalVotes]
  );

  const handleOptionClick = useCallback(
    (optionId: number) => {
      if (showResult || poll.isClosed) return;
      setSelectedOptionId(optionId);
    },
    [showResult, poll.isClosed]
  );

  const handleVote = useCallback(() => {
    if (!selectedOptionId || isVoting) return;
    vote(selectedOptionId);
  }, [selectedOptionId, isVoting, vote]);

  const remainingText =
    poll.endsAt && !poll.isClosed ? formatRemainingTime(poll.endsAt, t) : null;

  return (
    <div css={wrapperCSS}>
      <div css={optionsListCSS}>
        {poll.options.map((option) => {
          const isSelected = showResult
            ? option.id === poll.myVoteOptionId
            : option.id === selectedOptionId;
          const percentage = getPercentage(option.voteCount);
          const isWinner =
            showResult &&
            totalVotes > 0 &&
            (option.voteCount ?? 0) === maxVoteCount;

          return (
            <button
              key={option.id}
              type="button"
              css={optionBtnCSS(isSelected, showResult, isWinner)}
              onClick={() => handleOptionClick(option.id)}
              disabled={showResult}
            >
              {showResult && <div css={optionBarCSS(percentage, isWinner)} />}
              <div css={optionContentCSS}>
                <div css={optionTextCSS}>
                  {isSelected && showResult && (
                    <span css={checkMarkCSS}>✓</span>
                  )}
                  <span>{option.content}</span>
                </div>
                {showResult && (
                  <span css={voteCountCSS(isWinner)}>{percentage}%</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div css={footerCSS}>
        <div css={footerLeftCSS}>
          <span css={totalVotesCSS}>
            {showResult ? t('{{count}}명 투표', { count: totalVotes }) : ''}
          </span>
          {remainingText && <span css={remainingTimeCSS}>{remainingText}</span>}
        </div>
        {poll.isClosed ? (
          <span css={closedBadgeCSS}>{t('마감됨')}</span>
        ) : (
          !hasVoted && (
            <button
              type="button"
              css={voteBtnCSS}
              disabled={!selectedOptionId || isVoting}
              onClick={handleVote}
            >
              {t('투표하기')}
            </button>
          )
        )}
      </div>
    </div>
  );
};

export default PollDisplay;
